import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    accountActions,
    organizationActions,
    entityActions,
    businessActions,
    allocationActions,
} from '../../redux/actions/';
import { Table, Row, Col, Button, Typography, message, Space, Input, Empty } from 'antd';
import AllocationModal from '../../components/AllocationModal';
import OverlayedLoader from '../../components/OverlayedLoader';
import RenderUpdater from '../../components/RenderUpdater';
import { parseAllocationsData } from '../../utils/allocations/allocationsUtils';
import { serializeForApi } from '../../utils/allocations/serializeForApi';
import { buildCalculationAccountStructure } from '../../utils/accountStructure/accountStructureUtils';
import { useAvailableHeight } from '../../hooks/useAvailableHeight';

import CopyAllocationsModal, {
    getBusinessCaseId,
    getBusinessCaseName,
    allocationSignature,
} from '../../components/CopyAllocationsModal';

const { Title, Text } = Typography;

const UNASSIGNED_KEY = '__unassigned__';
const UNASSIGNED_LABEL = 'Not in a business case';

const isUnassigned = (row) => {
    const id = row?.businessCaseId;
    return id == null || id === '' || String(id) === '0';
};

const Allocations = () => {
    const dispatch = useDispatch();
    const businessCases = useSelector((state) => state.business.businessCases);
    const entities = useSelector((state) => state.entities.entities);
    const accountStructure = useSelector((state) => state.accounts.accountStructure);
    const calculationAccountStructure = useMemo(
        () => buildCalculationAccountStructure(accountStructure),
        [accountStructure],
    );

    const allocations = useSelector((state) => state.allocations.allocations);

    const fetchingStructure = useSelector((state) => state.accounts.fetchingStructure);
    const fetchingEntities = useSelector((state) => state.entities.fetchingEntities);
    const fetchingAllocations = useSelector((state) => state.allocations.fetchingAllocations);

    const creatingAllocations = useSelector((state) => state.allocations.creatingAllocations);
    const deletingAllocations = useSelector((state) => state.allocations.deletingAllocations);
    const updatingAllocations = useSelector((state) => state.allocations.updatingAllocations);
    const updatingOrganization = useSelector((state) => state.organization.updatingOrganization);

    const copyingAllocations = useSelector((state) => state.allocations.copyingAllocations);
    const copyProgress = useSelector((state) => state.allocations.copyProgress);

    const [isAllocationModalOpen, setIsAllocationModalOpen] = useState(false);
    const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
    const [allocation, setAllocation] = useState(null);
    const [edit, setEdit] = useState(false);

    const [selectedGroupKey, setSelectedGroupKey] = useState(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [search, setSearch] = useState('');
    const [originalEntities, setOriginalEntities] = useState(null);

    const [overviewHeaderRef, overviewScrollY] = useAvailableHeight({
        debugLabel: 'overview',
        debugOldGuess: 260,
    });
    const [detailHeaderRef, detailScrollY] = useAvailableHeight({
        debugLabel: 'detail',
        debugOldGuess: 320,
    });

    useEffect(() => {
        dispatch(entityActions.getEntities());
        dispatch(businessActions.getBusinessCases());
        dispatch(accountActions.getAccountStructure());
        dispatch(organizationActions.getOrganization());
        dispatch(allocationActions.getAllocations());
    }, [dispatch]);

    const dataReady =
        allocations != null &&
        businessCases != null &&
        entities != null &&
        calculationAccountStructure != null;

    const flatRows = useMemo(() => {
        if (!dataReady) return [];
        return parseAllocationsData(
            allocations,
            businessCases,
            entities,
            calculationAccountStructure,
        );
    }, [dataReady, allocations, businessCases, entities, calculationAccountStructure]);

    // One entry per business case, including ones with no allocations yet,
    // plus a bucket for allocations that belong to no business case.
    const groups = useMemo(() => {
        const byKey = new Map();

        (businessCases ?? []).forEach((bc) => {
            const key = getBusinessCaseId(bc);
            byKey.set(key, {
                key,
                businessCaseId: key,
                name: getBusinessCaseName(bc),
                rows: [],
            });
        });

        flatRows.forEach((row) => {
            const key = isUnassigned(row) ? UNASSIGNED_KEY : String(row.businessCaseId);
            if (!byKey.has(key)) {
                byKey.set(key, {
                    key,
                    businessCaseId: key === UNASSIGNED_KEY ? null : key,
                    name: key === UNASSIGNED_KEY ? UNASSIGNED_LABEL : row.businessCaseName || key,
                    rows: [],
                });
            }
            byKey.get(key).rows.push(row);
        });

        return [...byKey.values()]
         .map((g) => ({
                ...g,
                rows: [...g.rows].sort(
                    (a, b) => Number(a.allocation_id ?? a.key) - Number(b.allocation_id ?? b.key)
                ),
            }))
        
        .sort((a, b) => {
            if (a.key === UNASSIGNED_KEY) return 1;
            if (b.key === UNASSIGNED_KEY) return -1;
            return a.name.localeCompare(b.name);
        });
    }, [businessCases, flatRows]);

    const visibleGroups = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return groups;
        return groups.filter((g) => g.name.toLowerCase().includes(term));
    }, [groups, search]);

    // Keep a valid selection as data loads or changes.
    useEffect(() => {
        if (groups.length === 0) return;
        if (selectedGroupKey && groups.some((g) => g.key === selectedGroupKey)) return;
        setSelectedGroupKey(groups[0].key);
    }, [groups, selectedGroupKey]);

    const selectedGroup = useMemo(
        () => groups.find((g) => g.key === selectedGroupKey) ?? null,
        [groups, selectedGroupKey],
    );

    const detailRows = selectedGroup?.rows ?? [];

    const handleSelectGroup = (key) => {
        setSelectedGroupKey(key);
        setSelectedRowKeys([]);
    };

    const toStr = (v) => (v == null ? '' : String(v));

    // The shape AllocationModal and serializeForApi expect.
    const toModalShape = (r) => ({
        allocationId: String(r.allocation_id ?? r.key),
        businessCaseId: toStr(r.businessCaseId),
        entityId: toStr(r.entityId),
        accountId: toStr(r.accountId),
        targetEntityId: toStr(r.targetEntityId),
        targetAccountId: toStr(r.targetAccountId),
        scenarioId: Array.isArray(r.scenarioId) ? r.scenarioId.map(toStr) : [],
        targetScenarioId: Array.isArray(r.targetScenarioId) ? r.targetScenarioId.map(toStr) : [],
    });

    // Finds an existing allocation in the same business case with identical
    // entity, account, scenarios and targets. Pass the current allocation's id
    // when editing so a row doesn't match itself.
    const findDuplicate = (candidate, ignoreAllocationId = null) => {
        const signature = allocationSignature(candidate);
        const businessCaseId = String(candidate.businessCaseId ?? '');

        return (
            flatRows.find((row) => {
                if (
                    ignoreAllocationId != null &&
                    String(row.allocation_id ?? row.key) === String(ignoreAllocationId)
                ) {
                    return false;
                }
                if (String(row.businessCaseId ?? '') !== businessCaseId) return false;
                return allocationSignature(row) === signature;
            }) ?? null
        );
    };

    const overviewColumns = [
        {
            title: 'Business case',
            dataIndex: 'name',
            key: 'name',
            ellipsis: true,
            render: (value, record) => (
                <Text type={record.key === UNASSIGNED_KEY ? 'secondary' : undefined}>{value}</Text>
            ),
        },
        {
            title: 'Allocations',
            key: 'count',
            width: 100,
            align: 'right',
            render: (_, record) => (
                <Text type={record.rows.length === 0 ? 'secondary' : undefined}>
                    {record.rows.length}
                </Text>
            ),
        },
    ];

    const detailColumns = [
        { title: 'Entity', dataIndex: 'entityName', key: 'entityName', width: 240 },
        { title: 'Scenario', dataIndex: 'scenarioNames', key: 'scenarioNames', width: 240 },
        { title: 'Account', dataIndex: 'accountName', key: 'accountName', width: 240,
             render: (value, record) =>
                record.accountNumber ? (
                    <Space size={6}>
                        <Text type="secondary">{record.accountNumber}</Text>
                        <Text>{value}</Text>
                    </Space>
                ) : (
                    value
                ),
        },
        {
            title: 'Target Entity',
            dataIndex: 'targetEntityName',
            key: 'targetEntityName',
            width: 240,
        },
        {
            title: 'Target Scenario',
            dataIndex: 'targetScenarioNames',
            key: 'targetScenarioNames',
            width: 240,
        },
        {
            title: 'Target Account',
            dataIndex: 'targetAccountName',
            key: 'targetAccountName',
            width: 240,
            render: (value, record) =>
                record.accountNumber ? (
                    <Space size={6}>
                        <Text type="secondary">{record.targetAccountNumber}</Text>
                        <Text>{value}</Text>
                    </Space>
                ) : (
                    value
                ),
        },
    ];

    const handleOpenAddAllocation = () => {
        setAllocation({
            businessCaseId:
                selectedGroup && selectedGroup.key !== UNASSIGNED_KEY
                    ? String(selectedGroup.businessCaseId)
                    : '0',
            entityId: null,
            scenarioId: [],
            accountId: '0',
            targetEntityId: null,
            targetScenarioId: [],
            targetAccountId: '0',
        });
        setIsAllocationModalOpen(true);
        setEdit(false);
        setOriginalEntities(null);
    };

    const handleCreateAllocation = (payload) => {
        if (creatingAllocations) return false;

        if (findDuplicate(allocation)) {
            message.error('This allocation already exists in this business case.');
            return false;
        }

        dispatch(allocationActions.createAllocation(payload));
        return true;
    };


    const handleOpenEditAllocation = (row) => {
        const shaped = toModalShape(row);
            setOriginalEntities({
                entityId: shaped.entityId,
                targetEntityId: shaped.targetEntityId,
            });
            setAllocation(shaped);
            setIsAllocationModalOpen(true);
            setEdit(true);
        };


    const handleUpdateAllocation = () => {
        if (selectedRowKeys.length !== 1) {
            message.warning('Select exactly one allocation to update.');
            return;
        }
        const row = detailRows.find((r) => (r.allocation_id ?? r.key) === selectedRowKeys[0]);
        if (row) handleOpenEditAllocation(row);
    };


    const handleEditAllocation = (nextAllocation) => {
        if (updatingAllocations) return false;

        if (findDuplicate(nextAllocation, nextAllocation.allocationId)) {
            message.error('Another allocation in this business case is already identical.');
            return false;
        }

        dispatch(
            allocationActions.updateAllocation(
                Number(nextAllocation.allocationId),
                serializeForApi(nextAllocation),
            ),
        );
        return true;
    };

    const handleDeleteAllocation = () => {
        if (selectedRowKeys.length === 0) {
            message.warning('Select at least one allocation to delete.');
            return;
        }
        if (deletingAllocations) return;

        selectedRowKeys.forEach((id) => {
            dispatch(allocationActions.deleteAllocation(Number(id)));
        });
        setSelectedRowKeys([]);
    };

    const rowsToCopy = useMemo(() => {
        if (selectedRowKeys.length === 0) return detailRows;
        const keys = new Set(selectedRowKeys);
        return detailRows.filter((r) => keys.has(r.allocation_id ?? r.key));
    }, [selectedRowKeys, detailRows]);

    const handleOpenCopy = () => {
        if (rowsToCopy.length === 0) {
            message.warning('This business case has no allocations to copy.');
            return;
        }
        setIsCopyModalOpen(true);
    };
    
    const handleConfirmCopy = async (targetBusinessCaseId, rows) => {
    if (copyingAllocations || !targetBusinessCaseId) return;

    const payloads = rows.map((row) => {
        // eslint-disable-next-line no-unused-vars
        const { allocationId, ...fields } = toModalShape(row);
        return serializeForApi({ ...fields, businessCaseId: String(targetBusinessCaseId) });
    });

    const { created, failed } = await dispatch(allocationActions.copyAllocations(payloads));

    setIsCopyModalOpen(false);
    setSelectedRowKeys([]);

    if (failed.length > 0) {
        message.warning(`Copied ${created} of ${payloads.length}. ${failed.length} failed.`);
    } else {
        message.success(`Copied ${created} allocation${created === 1 ? '' : 's'}.`);
    }
};

    const renderDetail = () => {
        if (!selectedGroup) {
            return <Empty description="Select a business case" />;
        }

        return (
            <>
                <div ref={detailHeaderRef}>
                    <Row align="middle" style={{ marginBottom: 12 }}>
                        <Col flex="auto">
                            <Title level={4} style={{ margin: 0 }}>
                                {selectedGroup.name}
                            </Title>
                            <Text type="secondary">
                                {detailRows.length} allocation{detailRows.length === 1 ? '' : 's'}
                                {selectedRowKeys.length > 0
                                    ? ` · ${selectedRowKeys.length} selected`
                                    : ''}
                            </Text>
                        </Col>
                        <Col>
                            <Space>
                                <Button onClick={handleOpenAddAllocation}>Add allocation</Button>
                                <Button onClick={handleOpenCopy}>
                                    {selectedRowKeys.length > 0
                                        ? `Copy ${selectedRowKeys.length} to…`
                                        : 'Copy all to…'}
                                </Button>
                                <Button type="primary" onClick={handleUpdateAllocation}>
                                    Update
                                </Button>
                                <Button danger onClick={handleDeleteAllocation}>
                                    Delete
                                </Button>
                            </Space>
                        </Col>
                    </Row>

                    <Row>
                        <Col span={12}>
                            <Text strong>From</Text>
                        </Col>
                        <Col span={12}>
                            <Text strong>To</Text>
                        </Col>
                    </Row>
                </div>

                <Table
                    rowKey={(r) => r.allocation_id ?? r.key}
                    rowSelection={{
                        selectedRowKeys,
                        onChange: setSelectedRowKeys,
                    }}
                    dataSource={detailRows}
                    columns={detailColumns}
                    pagination={false}
                    size="small"
                    scroll={{ x: 'max-content', y: detailScrollY }}
                    onRow={(record) => ({
                        onDoubleClick: () => handleOpenEditAllocation(record),
                    })}
                    locale={{ emptyText: <Empty description="No allocations here yet" /> }}
                />
            </>
        );
    };

    if (fetchingStructure || fetchingEntities || fetchingAllocations || !dataReady) {
        return <OverlayedLoader />;
    }
    if (updatingOrganization) {
        return <RenderUpdater />;
    }

    return (
        <Row gutter={24} style={{ padding: 24 }}>
            <Col flex="320px" style={{ minWidth: 0 }}>
                <div ref={overviewHeaderRef}>
                    <Title level={4} style={{ marginTop: 0 }}>
                        Business cases
                    </Title>
                    <Input.Search
                        placeholder="Find a business case"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        allowClear
                        style={{ marginBottom: 12 }}
                    />
                </div>
                <Table
                    rowKey="key"
                    dataSource={visibleGroups}
                    columns={overviewColumns}
                    pagination={false}
                    size="small"
                    scroll={{ y: overviewScrollY }}
                    onRow={(record) => ({
                        onClick: () => handleSelectGroup(record.key),
                        style: { cursor: 'pointer' },
                    })}
                    rowClassName={(record) =>
                        record.key === selectedGroupKey ? 'allocations-group-selected' : ''
                    }
                />
            </Col>

            <Col flex="auto" style={{ minWidth: 0 }}>
                {renderDetail()}
            </Col>

            {allocation != null ? (
                <AllocationModal
                    businessCases={businessCases}
                    entities={entities}
                    accountStructure={calculationAccountStructure}
                    setAllocation={setAllocation}
                    allocation={allocation}
                    isAllocationModalOpen={isAllocationModalOpen}
                    setIsAllocationModalOpen={setIsAllocationModalOpen}
                    handleCreateAllocation={handleCreateAllocation}
                    handleEditAllocation={handleEditAllocation}
                    handleDeleteAllocation={handleDeleteAllocation}
                    edit={edit}
                    setEdit={setEdit}
                    originalEntities={originalEntities}
                />
            ) : null}

            <CopyAllocationsModal
                open={isCopyModalOpen}
                sourceName={selectedGroup?.name}
                sourceBusinessCaseId={selectedGroup?.businessCaseId}
                rowsToCopy={rowsToCopy}
                allRows={flatRows}
                businessCases={businessCases}
                entities={entities}
                copying={copyingAllocations}
                onCancel={() => setIsCopyModalOpen(false)}
                onConfirm={handleConfirmCopy}
            />
        </Row>
    );
};

export default Allocations;
